import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Button } from "@/components/ui/button";
import { COMPANY_MULTI_SELECT_KEYS } from "@/components/portal/steps/application-step";
import {
  FormFieldsEditor,
  filterEmptyFields,
  type FormFieldMap,
} from "@/components/settings/form-fields-editor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { IOpportunityForm } from "@/types/data-models";
import { CalendarIcon, Copy, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { format } from "date-fns";
import posthog from "posthog-js";

// Intake dates are date-only ("YYYY-MM-DD"). BE stores them as Date,
// so the API may hand back an ISO timestamp — take the date portion only.
function toIntakeDate(value: string | undefined): string {
  return value ? value.split("T")[0] ?? "" : "";
}

function formatIntakeDisplay(value: string): string {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, "PP");
}

function formatCheque(value: number, currency?: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function IntakeDateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "w-full justify-start font-normal",
              !value && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? formatIntakeDisplay(value) : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(next) => {
              onChange(next ? format(next, "yyyy-MM-dd") : "");
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ApplicationsSettings() {
  const { workspace, isLoading, activeWorkspaceId, updateWorkspaceMutation } =
    useWorkspaceSettings();
  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();
  const workspaceId = urlWorkspaceId || activeWorkspaceId;

  const [activeTab, setActiveTab] = useState<"landing-page" | "submission-form">(
    "landing-page",
  );

  // Landing Page tab state
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [allowSubmissions, setAllowSubmissions] = useState(false);
  const [description, setDescription] = useState("");
  const [startIntake, setStartIntake] = useState("");
  const [endIntake, setEndIntake] = useState("");
  const [resources, setResources] = useState("");

  // Submission Form tab state
  const [fields, setFields] = useState<IOpportunityForm>({});

  // revenue_model / business_model are collected in the portal's Company
  // Info step, so the admin editor shouldn't expose them as configurable.
  const stripCompanyKeys = (form: IOpportunityForm): IOpportunityForm =>
    Object.fromEntries(
      Object.entries(form).filter(
        ([k]) =>
          !COMPANY_MULTI_SELECT_KEYS.includes(
            k as (typeof COMPANY_MULTI_SELECT_KEYS)[number],
          ),
      ),
    ) as IOpportunityForm;

  useEffect(() => {
    if (!workspace) return;
    setPortalEnabled(!(workspace.opportunity_private ?? true));
    setAllowSubmissions(workspace.has_applicant_room || false);
    setDescription(workspace.opportunity_description ?? "");
    setStartIntake(toIntakeDate(workspace.start_intake_date));
    setEndIntake(toIntakeDate(workspace.end_intake_date));
    setResources(workspace.prize ?? "");
    setFields(stripCompanyKeys(workspace.opportunity_form || {}));
  }, [workspace]);

  const slug = workspace?.portal_slug ?? "";
  const portalUrl = slug ? `${window.location.origin}/p/${slug}` : "";
  const hasSlug = Boolean(slug);

  const landingDirty =
    allowSubmissions !== (workspace?.has_applicant_room || false) ||
    description !== (workspace?.opportunity_description ?? "") ||
    startIntake !== toIntakeDate(workspace?.start_intake_date) ||
    endIntake !== toIntakeDate(workspace?.end_intake_date) ||
    resources !== (workspace?.prize ?? "");

  const formDirty =
    JSON.stringify(
      filterEmptyFields(fields as FormFieldMap) as IOpportunityForm,
    ) !== JSON.stringify(stripCompanyKeys(workspace?.opportunity_form || {}));

  const isDirty = activeTab === "landing-page" ? landingDirty : formDirty;

  const handlePortalToggle = (checked: boolean) => {
    if (checked && !hasSlug) {
      toast.error("Set your portal URL in General Settings first.");
      return;
    }
    setPortalEnabled(checked);
    updateWorkspaceMutation.mutate(
      { opportunity_private: !checked },
      {
        onSuccess: () => {
          toast.success(
            checked ? "Public portal enabled" : "Public portal disabled",
          );
          posthog.capture("settings_applications_portal_toggled", {
            workspace_id: activeWorkspaceId,
            portal_enabled: checked,
          });
        },
        onError: () => {
          setPortalEnabled(!checked);
          toast.error("Failed to update portal");
        },
      },
    );
  };

  const handleSavePortal = () => {
    if (!hasSlug && portalEnabled) {
      toast.error(
        "Set your portal URL in General Settings before enabling the portal.",
      );
      return;
    }
    const toastId = toast.loading("Updating landing page...");
    updateWorkspaceMutation.mutate(
      {
        has_applicant_room: allowSubmissions,
        opportunity_description: description,
        start_intake_date: startIntake || undefined,
        end_intake_date: endIntake || undefined,
        prize: resources,
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          toast.success("Landing page saved");
          posthog.capture("settings_applications_landing_updated", {
            workspace_id: activeWorkspaceId,
            portal_enabled: portalEnabled,
            allow_submissions: allowSubmissions,
          });
        },
        onError: () => toast.dismiss(toastId),
      },
    );
  };

  const handleSaveForm = () => {
    const filtered = filterEmptyFields(
      fields as FormFieldMap,
    ) as IOpportunityForm;
    const toastId = toast.loading("Updating submission form...");
    updateWorkspaceMutation.mutate(
      { opportunity_form: filtered },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          toast.success("Submission form saved");
          posthog.capture("settings_applications_form_updated", {
            workspace_id: activeWorkspaceId,
            field_count: Object.keys(filtered).length,
          });
        },
        onError: () => toast.dismiss(toastId),
      },
    );
  };

  const copyPortalUrl = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    toast.success("Copied to clipboard");
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
      <h1 className="text-lg font-semibold text-foreground">Applications</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Configure your public application portal and submission form.
      </p>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setActiveTab(v as "landing-page" | "submission-form")
        }
        className="mt-6"
      >
        <TabsList>
          <TabsTrigger value="landing-page">Landing Page</TabsTrigger>
          <TabsTrigger value="submission-form">Submission Form</TabsTrigger>
        </TabsList>

        {/* ─── Landing Page tab ─────────────────────────────────────── */}
        <TabsContent value="landing-page" className="mt-6 space-y-8">
          {/* Portal toggle + URL */}
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Public Portal
            </h2>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">Public portal</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Lets startups discover you and submit applications.
                  </p>
                </div>
                <Switch
                  checked={portalEnabled}
                  onCheckedChange={handlePortalToggle}
                  disabled={updateWorkspaceMutation.isPending}
                />
              </div>

              {!hasSlug && (
                <div className="mt-3 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  No portal URL set.{" "}
                  <Link
                    to={`/${workspaceId}/settings/general`}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Set one in General Settings →
                  </Link>
                </div>
              )}

              {portalEnabled && hasSlug && (
                <>
                  <div className="mt-4 border-t border-border pt-4">
                    <Label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                      Public URL
                    </Label>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        readOnly
                        value={portalUrl}
                        className="flex-1 bg-muted font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={copyPortalUrl}
                        title="Copy URL"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => window.open(portalUrl, "_blank")}
                        title="Open in new tab"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Change the slug in{" "}
                      <Link
                        to={`/${workspaceId}/settings/general`}
                        className="text-foreground underline-offset-4 hover:underline"
                      >
                        General Settings
                      </Link>
                      .
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                    <div>
                      <div className="text-sm font-medium">
                        Allow workspace submissions
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Lets logged-in workspace members submit applications too.
                      </p>
                    </div>
                    <Switch
                      checked={allowSubmissions}
                      onCheckedChange={setAllowSubmissions}
                    />
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Cross-page breadcrumb */}
          <div className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">
                Logo, workspace name, and website
              </span>{" "}
              for your landing page are configured in General Settings.
            </p>
            <Link
              to={`/${workspaceId}/settings/general`}
              className="mt-1 inline-block text-xs font-medium text-foreground underline-offset-4 hover:underline"
            >
              Open General Settings →
            </Link>
          </div>

          {/* Description */}
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Description
            </h2>
            <div className="rounded-lg border border-border bg-card p-4">
              <Label>What founders see on your landing page</Label>
              <div className="mt-2">
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Tell founders what you invest in, what you offer, and why they should apply..."
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Rendered at the top of <span className="font-mono">/p/{slug || "<slug>"}</span>.
              </p>
            </div>
          </section>

          {/* Intake window */}
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Intake Window
            </h2>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="grid grid-cols-2 gap-4">
                <IntakeDateField
                  id="start-intake"
                  label="Start date"
                  value={startIntake}
                  onChange={setStartIntake}
                />
                <IntakeDateField
                  id="end-intake"
                  label="End date"
                  value={endIntake}
                  onChange={setEndIntake}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Optional. Applicants see these dates on your landing page.
              </p>
            </div>
          </section>

          {/* Resources */}
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Support &amp; Resources
            </h2>
            <div className="rounded-lg border border-border bg-card p-4">
              <Label>What you offer applicants</Label>
              <div className="mt-2">
                <RichTextEditor
                  value={resources}
                  onChange={setResources}
                  placeholder="Describe the support and resources you offer..."
                />
              </div>
            </div>
          </section>

          {/* Investment details (read-only preview, edited on Thesis) */}
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Investment Details
            </h2>
            <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm">
              <div className="text-xs text-muted-foreground">
                Shown on{" "}
                <span className="font-mono">/p/{slug || "<slug>"}</span> as
                "Investment Details." Edit on{" "}
                <Link
                  to={`/${workspaceId}/settings/thesis`}
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Thesis
                </Link>
                .
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 font-mono text-sm">
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Currency
                  </div>
                  <div className="mt-0.5 text-foreground">
                    {workspace?.currency || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Min cheque
                  </div>
                  <div className="mt-0.5 text-foreground">
                    {workspace?.min_cheque_size
                      ? formatCheque(
                          workspace.min_cheque_size,
                          workspace.currency,
                        )
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Max cheque
                  </div>
                  <div className="mt-0.5 text-foreground">
                    {workspace?.max_cheque_size
                      ? formatCheque(
                          workspace.max_cheque_size,
                          workspace.currency,
                        )
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </section>

        </TabsContent>

        {/* ─── Submission Form tab ──────────────────────────────────── */}
        <TabsContent value="submission-form" className="mt-6">
          {!portalEnabled && (
            <div className="mb-6 rounded-md border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Your public portal is off.
                </span>{" "}
                You can keep editing the form, but no one will see it until you
                turn the portal on.
              </p>
              <button
                type="button"
                onClick={() => setActiveTab("landing-page")}
                className="mt-1 text-xs font-medium text-foreground underline-offset-4 hover:underline"
              >
                Open Landing Page tab →
              </button>
            </div>
          )}

          {portalEnabled && !allowSubmissions && (
            <div className="mb-6 rounded-md border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">
                  Workspace submissions are off.
                </span>{" "}
                Public applicants can still submit through{" "}
                <span className="font-mono">/p/{slug || "<slug>"}</span>, but
                logged-in workspace members cannot.
              </p>
              <button
                type="button"
                onClick={() => setActiveTab("landing-page")}
                className="mt-1 text-xs font-medium text-foreground underline-offset-4 hover:underline"
              >
                Open Landing Page tab →
              </button>
            </div>
          )}

          <section>
            <h2 className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              Submission Form Fields
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Configure fields for new company submissions to your workspace.
            </p>
            <FormFieldsEditor
              fields={fields as FormFieldMap}
              onChange={(next) => setFields(next as IOpportunityForm)}
              addLabel="Add Field"
            />
          </section>

        </TabsContent>
      </Tabs>
    </div>
    <div className="sticky bottom-0 border-t border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-2xl justify-end px-6 py-3">
        <Button
          onClick={
            activeTab === "landing-page" ? handleSavePortal : handleSaveForm
          }
          disabled={updateWorkspaceMutation.isPending || !isDirty}
        >
          {updateWorkspaceMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
    </>
  );
}
